import {
  type PmBulletList,
  type PmCodeBlock,
  type PmDoc,
  type PmInline,
  type PmListItem,
  type PmMark,
  PmMarkType,
  type PmNode,
  PmNodeType,
  type PmOrderedList,
  type PmParagraph,
  type PmTemplateVar,
  type PmText,
  type TemplateVarType,
  type TemplateVarValue,
  type VariableKind,
} from "@storytell/types/richText";

/** Optional mention extraction so quoted/unquoted names render as VariableV1 pills (e.g. in improved prompt). */
export type ParseTemplatePromptMentionsOpts = {
  extractAssets?: { id: string; name: string }[];
  extractConcepts?: { id: string; name: string }[];
  extractLabels?: { id: string; name: string }[];
  extractViews?: { id: string; name: string }[];
  extractAgents?: { id: string; name: string }[];
  /** Persona/skill pills; displayName is matched in text (e.g. "Finance Advisor"), name is the registry key for submit. */
  extractSkills?: { id: string; name: string; displayName: string }[];
};

/**
 * Parses a template prompt string into a ProseMirror document.
 *
 * Supports:
 * - Template variables: #{text:name}, #{file:name}, #{opts:name:["opt1", "opt2"]}
 *   For paragraph/text/file/speak, the name may contain colons (e.g. #{paragraph:Optional: Describe...}:optional).
 * - Single newline = new paragraph
 * - Double newline = empty paragraph (visual gap) + new paragraph
 * - Bullet lists with "- " prefix
 * - Ordered lists with "1. ", "2. ", etc. prefix
 *
 * @example
 * ```
 * const template = `Hello #{text:username}, please review:
 *
 * - First item
 * - Second item with #{file:document}
 *
 * Select format: #{opts:format:["PDF", "Word", "Text"]}`;
 *
 * const doc = parseTemplatePrompt(template);
 * ```
 */

// Regexes to match template variables. We use two patterns so names can contain colons for
// paragraph/text/file/speak (e.g. #{paragraph:Optional: Describe the group...}:optional).
//
// 1) opts: name must not contain colon (name is between first and second colon); options follow.
//    #{opts:name:["opt1","opt2"]} or #{opts:name:Option1} or :optional, {fallback:...}
// 2) text/paragraph/file/speak: name is everything until closing }; colons allowed in name.
//    #{paragraph:Optional: Describe in detail.}:optional
const OPTS_VAR_REGEX = /#\{opts:([^:]+):(\[[\s\S]*?\]|[^}]+)\}(?::optional)?(?:\{fallback:([^}]*)\})?/g;
const OTHER_VAR_REGEX = /#\{(text|paragraph|file|speak):([^}]+)\}(?::optional)?(?:\{fallback:([^}]*)\})?/g;

interface ParsedTemplateVar {
  varType: TemplateVarType;
  name: string;
  options: string[];
  required: boolean;
  fallback?: string;
}

function parseTemplateVarMatch(
  type: string,
  name: string,
  optionsJson?: string,
  isOptional?: boolean,
  fallbackValue?: string
): ParsedTemplateVar {
  const base = {
    name: name.trim(),
    required: !isOptional,
    fallback: fallbackValue !== undefined && fallbackValue !== "" ? fallbackValue : undefined,
  };

  if (type === "opts" && optionsJson) {
    const trimmed = optionsJson.trim();
    if (trimmed.startsWith("[")) {
      try {
        const options = JSON.parse(trimmed) as string[];
        return { varType: "dropdown", options, ...base };
      } catch {
        return { varType: "dropdown", options: [], ...base };
      }
    }
    // Plain format: "Board Meeting" or "Board Meeting, Budget Review"
    const options = trimmed
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return { varType: "dropdown", options, ...base };
  }

  if (type === "paragraph") return { varType: "paragraph", options: [], ...base };
  if (type === "text") return { varType: "text", options: [], ...base };
  if (type === "speak") return { varType: "record-audio", options: [], ...base };
  return { varType: "file-pick", options: [], ...base };
}

function getDefaultValue(varType: TemplateVarType): TemplateVarValue {
  switch (varType) {
    case "text":
    case "paragraph":
      return { text: "" };
    case "dropdown":
      return { value: "" };
    case "file-pick":
    case "record-audio":
      return { assetId: "", filename: "" };
  }
}

interface MentionState {
  nameToId: Map<string, string>;
  nameToType: Map<string, VariableKind>;
  nameToSkillData: Map<string, { id: string; name: string; displayName: string }>;
  regex: RegExp;
}

const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const longestFirst = (names: string[]) =>
  names
    .toSorted((a, b) => b.length - a.length)
    .map(escapeRegex)
    .join("|");

function buildMentionState(opts: ParseTemplatePromptMentionsOpts): MentionState | null {
  const nameToId = new Map<string, string>();
  const nameToType = new Map<string, VariableKind>();
  const nameToSkillData = new Map<string, { id: string; name: string; displayName: string }>();

  for (const a of opts.extractAssets ?? []) {
    nameToId.set(a.name, a.id);
    nameToType.set(a.name, "asset");
  }
  for (const c of opts.extractConcepts ?? []) {
    nameToId.set(c.name, c.id);
    nameToType.set(c.name, "concept");
  }
  for (const l of opts.extractLabels ?? []) {
    nameToId.set(l.name, l.id);
    nameToType.set(l.name, "label");
  }
  for (const v of opts.extractViews ?? []) {
    nameToId.set(v.name, v.id);
    nameToType.set(v.name, "view");
  }
  for (const a of opts.extractAgents ?? []) {
    nameToId.set(a.name, a.id);
    nameToType.set(a.name, "agent");
  }
  for (const s of opts.extractSkills ?? []) {
    const skillData = { id: s.id, name: s.name, displayName: s.displayName };
    // Register display name (e.g. "IT Director") for quoted matching
    if (s.displayName) {
      nameToId.set(s.displayName, s.id);
      nameToType.set(s.displayName, "skill");
      nameToSkillData.set(s.displayName, skillData);
    }
    // Register @name form (e.g. "@it") for explicit @ references only.
    // Bare registry names are NOT registered to avoid word-boundary regex
    // matching common English words (e.g. skill name "it" matching the word "it").
    // Skills should be referenced by ID via the @ mention picker, by display name
    // in quotes, or by @name — never by bare name in plain text.
    if (s.name) {
      const atName = `@${s.name}`;
      nameToId.set(atName, s.id);
      nameToType.set(atName, "skill");
      nameToSkillData.set(atName, skillData);
    }
  }

  const skillMentionNames = (opts.extractSkills ?? []).flatMap((s) => {
    const parts: string[] = [];
    if (s.displayName) parts.push(s.displayName);
    if (s.name) parts.push(`@${s.name}`);
    return parts;
  });

  const allNames = [
    ...(opts.extractAssets?.map((x) => x.name) ?? []),
    ...(opts.extractConcepts?.map((x) => x.name) ?? []),
    ...(opts.extractLabels?.map((x) => x.name) ?? []),
    ...(opts.extractViews?.map((x) => x.name) ?? []),
    ...(opts.extractAgents?.map((x) => x.name) ?? []),
    ...skillMentionNames,
  ].filter(Boolean);
  if (allNames.length === 0) return null;

  const namePattern = longestFirst(allNames);
  // Names are matched inside quotes (how VariableV1.renderText serializes pills, e.g. "Display name").
  // Bare word-boundary matching is intentionally omitted so plain prose containing a workspace
  // entity name (e.g. a pasted sentence mentioning "Chief" or "Linear" as labels) is not silently
  // turned into pills — pills should only come from explicit user actions or serialized pill text.
  const alternatives = [`"(${namePattern})"`, `'(${namePattern})'`];
  // The @name form needs no quotes: the @ is itself the explicit marker the rule above protects,
  // and the improver writes handles bare as often as it quotes display names — so these keys were
  // registered with no pattern that could reach them.
  const atNames = skillMentionNames.filter((name) => name.startsWith("@"));
  if (atNames.length > 0) {
    // Fenced both sides, so a handle sitting inside an address or prefixing a longer handle is
    // left alone.
    alternatives.push(`(?<![\\w.@-])(${longestFirst(atNames)})(?![\\w-])`);
  }
  const regex = new RegExp(alternatives.join("|"), "g");
  return { nameToId, nameToType, nameToSkillData, regex };
}

/**
 * Splits text nodes in already-parsed inlines by mention names, inserting VariableV1 nodes.
 * Preserves marks (e.g. bold) on each text segment so bold that spans across a mention is retained.
 */
function splitInlinesByMentions(inlines: PmInline[], opts: ParseTemplatePromptMentionsOpts): PmInline[] {
  const state = buildMentionState(opts);
  if (!state) return inlines;

  const { nameToId, nameToType, nameToSkillData, regex } = state;
  const result: PmInline[] = [];

  for (const node of inlines) {
    if (node.type !== PmNodeType.Text) {
      result.push(node);
      continue;
    }
    const textNode = node;
    const text = textNode.text;
    const marks = textNode.marks ?? [];
    // A link's text is its href, and URLs carry handles of their own — splitting one leaves half a
    // URL linked beside a pill for a skill nobody referenced.
    if (!text || marks.some((mark) => mark.type === PmMarkType.Link)) {
      result.push(node);
      continue;
    }

    const parts: PmInline[] = [];
    let lastIndex = 0;
    regex.lastIndex = 0;
    let match: RegExpExecArray | null = regex.exec(text);
    while (match !== null) {
      if (match.index > lastIndex) {
        const before = text.slice(lastIndex, match.index);
        if (before) parts.push({ type: PmNodeType.Text, text: before, marks });
      }
      const name = match[1] ?? match[2] ?? match[3];
      const id = name === undefined ? undefined : nameToId.get(name);
      const kind = name === undefined ? undefined : nameToType.get(name);
      if (name && id !== undefined && kind !== undefined) {
        const skillData = nameToSkillData.get(name);
        parts.push({
          type: PmNodeType.VariableV1,
          attrs: {
            type: kind,
            id,
            text: name,
            ...(skillData && { data: { displayName: skillData.displayName, name: skillData.name } }),
          },
        });
      } else if (name) {
        parts.push({ type: PmNodeType.Text, text: text.slice(match.index, regex.lastIndex), marks });
      }
      lastIndex = regex.lastIndex;
      match = regex.exec(text);
    }
    if (lastIndex < text.length) {
      const rest = text.slice(lastIndex);
      if (rest) parts.push({ type: PmNodeType.Text, text: rest, marks });
    }
    result.push(...(parts.length > 0 ? parts : [node]));
  }
  return result;
}

/** Placeholder used so bold can span across template variables (e.g. "**Create a battlecard for #{text:Name}.**"). */
const TEMPLATE_VAR_PLACEHOLDER = "\uFFFC";

/** Bold mark for use in parsed text nodes. */
const BOLD_MARK: PmMark = { type: PmMarkType.Bold };

/** Regex to detect http/https URLs so they can be wrapped in link marks and render as clickable pills. */
const URL_REGEX = /https?:\/\/[^\s<>"]+/g;

/**
 * Splits plain text into inline nodes, wrapping URL segments in link marks so they
 * render as clickable links in the prompt bar and in static prompt display.
 */
function splitTextWithUrlSegments(text: string): PmInline[] {
  const parts: PmInline[] = [];
  let lastIndex = 0;
  URL_REGEX.lastIndex = 0;
  let match = URL_REGEX.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      const before = text.slice(lastIndex, match.index);
      if (before) parts.push({ type: PmNodeType.Text, text: before });
    }
    const rawUrl = match[0];
    const href = rawUrl.replace(/[.,;:!?)]+$/, "");
    const trailingPunctuation = rawUrl.slice(href.length);
    parts.push({
      type: PmNodeType.Text,
      text: href,
      marks: [{ type: PmMarkType.Link, attrs: { href } }],
    });
    if (trailingPunctuation) parts.push({ type: PmNodeType.Text, text: trailingPunctuation });
    lastIndex = URL_REGEX.lastIndex;
    match = URL_REGEX.exec(text);
  }

  if (lastIndex < text.length) {
    const remaining = text.slice(lastIndex);
    if (remaining) parts.push({ type: PmNodeType.Text, text: remaining });
  }

  return parts.length > 0 ? parts : text ? [{ type: PmNodeType.Text, text }] : [];
}

/**
 * Parses markdown bold (**text** or __text__) in plain text and returns PmInline[] with bold marks.
 * URLs in plain segments are wrapped in link marks so they render as clickable pills.
 * Text may contain TEMPLATE_VAR_PLACEHOLDER; call expandPlaceholdersInInlines to replace with variable nodes.
 */
function parseMarkdownBold(text: string): PmInline[] {
  if (!text) return [];
  const result: PmInline[] = [];
  // Match **...** or __...__ (non-greedy); avoid matching across newlines
  const boldRegex = /\*\*([^*\n]+?)\*\*|__(?!_)([^_\n]+?)__/g;
  let lastEnd = 0;
  let match = boldRegex.exec(text);
  while (match !== null) {
    if (match.index > lastEnd) {
      const plain = text.slice(lastEnd, match.index);
      if (plain) result.push(...splitTextWithUrlSegments(plain));
    }
    const boldText = match[1] ?? match[2] ?? "";
    if (boldText) result.push({ type: PmNodeType.Text, text: boldText, marks: [BOLD_MARK] });
    lastEnd = match.index + match[0].length;
    match = boldRegex.exec(text);
  }
  if (lastEnd < text.length) {
    const plain = text.slice(lastEnd);
    if (plain) result.push(...splitTextWithUrlSegments(plain));
  }
  return result.length > 0 ? result : text ? splitTextWithUrlSegments(text) : [];
}

/**
 * Replaces TEMPLATE_VAR_PLACEHOLDER in text nodes with the corresponding template variable nodes.
 * Used when we ran parseMarkdownBold on a string that had variables replaced by placeholder so
 * bold could span across variables (e.g. "**Create a battlecard for #{text:Name}.**").
 */
function expandPlaceholdersInInlines(inlines: PmInline[], variables: { node: PmTemplateVar }[]): PmInline[] {
  if (variables.length === 0) return inlines;
  let varIndex = 0;
  const out: PmInline[] = [];
  for (const node of inlines) {
    if (node.type !== PmNodeType.Text) {
      out.push(node);
      continue;
    }
    const textNode = node;
    if (!textNode.text.includes(TEMPLATE_VAR_PLACEHOLDER)) {
      out.push(node);
      continue;
    }
    const parts = textNode.text.split(TEMPLATE_VAR_PLACEHOLDER);
    const marks = textNode.marks ?? [];
    for (let i = 0; i < parts.length; i++) {
      if (parts[i]) {
        out.push({ type: PmNodeType.Text, text: parts[i], marks } as PmText);
      }
      if (i < parts.length - 1 && varIndex < variables.length) {
        const v = variables[varIndex];
        if (v) out.push(v.node);
        varIndex++;
      }
    }
  }
  return out;
}

function parseInlineContent(text: string, mentionOpts?: ParseTemplatePromptMentionsOpts): PmInline[] {
  // Collect all template variable matches (from both opts and other-type regexes), then build
  // a string with placeholders so parseMarkdownBold sees **...** across variables.
  type MatchEntry = {
    match: RegExpExecArray;
    type: string;
    name: string;
    optionsJson?: string;
    fallbackValue?: string;
  };
  const allMatches: MatchEntry[] = [];

  const optsRegex = new RegExp(OPTS_VAR_REGEX.source, "g");
  let match: RegExpExecArray | null = optsRegex.exec(text);
  while (match !== null) {
    const [, name, optionsJson, fallbackValue] = match;
    if (name) allMatches.push({ match, type: "opts", name, optionsJson, fallbackValue });
    match = optsRegex.exec(text);
  }

  const otherRegex = new RegExp(OTHER_VAR_REGEX.source, "g");
  match = otherRegex.exec(text);
  while (match !== null) {
    const [, type, name, fallbackValue] = match;
    if (type && name) allMatches.push({ match, type, name, fallbackValue });
    match = otherRegex.exec(text);
  }

  allMatches.sort((a, b) => a.match.index - b.match.index);

  const matchesWithNodes: { match: RegExpExecArray; node: PmTemplateVar }[] = [];
  for (const entry of allMatches) {
    const isOptional = entry.match[0].includes(":optional");
    const parsed = parseTemplateVarMatch(entry.type, entry.name, entry.optionsJson, isOptional, entry.fallbackValue);
    matchesWithNodes.push({
      match: entry.match,
      node: {
        type: PmNodeType.TemplateVarV1,
        attrs: {
          varType: parsed.varType,
          name: parsed.name,
          options: parsed.options,
          value: getDefaultValue(parsed.varType),
          required: parsed.required,
          ...(parsed.fallback !== undefined && { fallback: parsed.fallback }),
        },
      },
    });
  }

  // Build string with one placeholder per variable (replace from end so indices stay valid)
  let replaced = text;
  for (let i = matchesWithNodes.length - 1; i >= 0; i--) {
    const item = matchesWithNodes[i];
    if (!item) continue;
    const m = item.match;
    replaced = replaced.slice(0, m.index) + TEMPLATE_VAR_PLACEHOLDER + replaced.slice(m.index + m[0].length);
  }

  // Parse bold first so **...** is recognized even when it spans across template vars or mentions
  let result: PmInline[] = parseMarkdownBold(replaced);

  const variables = matchesWithNodes.map(({ node }) => ({ node }));
  result = expandPlaceholdersInInlines(result, variables);

  if (mentionOpts) result = splitInlinesByMentions(result, mentionOpts);

  return result;
}

function createParagraph(content: PmInline[]): PmParagraph {
  return {
    type: PmNodeType.Paragraph,
    content,
  };
}

function createListItem(lineContent: string, mentionOpts?: ParseTemplatePromptMentionsOpts): PmListItem {
  const inlines = parseInlineContent(lineContent, mentionOpts);
  return {
    type: PmNodeType.ListItem,
    content: [createParagraph(inlines)],
  };
}

function createCodeBlock(codeLines: string[], language: string): PmCodeBlock {
  const text = codeLines.join("\n");
  return {
    type: PmNodeType.CodeBlock,
    attrs: language ? { language } : {},
    content: text ? [{ type: PmNodeType.Text, text }] : [],
  };
}

const FENCE_OPEN_REGEX = /^```([a-zA-Z0-9+#-]*)$/;

function isBulletLine(line: string): boolean {
  return line.trimStart().startsWith("- ");
}

function isOrderedListLine(line: string): boolean {
  return /^\s*\d+\.\s+/.test(line);
}

function createBulletList(lines: string[], mentionOpts?: ParseTemplatePromptMentionsOpts): PmBulletList {
  const items: PmListItem[] = lines.map((line) => {
    const content = line.trimStart().slice(2); // Remove "- "
    return createListItem(content, mentionOpts);
  });
  return {
    type: PmNodeType.BulletList,
    content: items,
  };
}

function createOrderedList(lines: string[], mentionOpts?: ParseTemplatePromptMentionsOpts): PmOrderedList {
  const orderedListRegex = /^\s*\d+\.\s+/;
  const items: PmListItem[] = lines.map((line) => {
    const content = line.replace(orderedListRegex, ""); // Remove "1. " etc
    return createListItem(content, mentionOpts);
  });
  return {
    type: PmNodeType.OrderedList,
    content: items,
  };
}

/**
 * Removes double quotes that wrap a full template variable (#{...} or @{...}).
 * Used when improved-prompt API returns e.g. "#{text:Person's name}" so we parse as a variable, not literal quotes.
 * Does not strip quotes around @-mentions (e.g. file names); only strips when the quoted span is exactly a template var.
 */
function stripQuotesAroundTemplateVars(text: string): string {
  let i = 0;
  let result = "";
  while (i < text.length) {
    const isQuotedVar = text[i] === '"' && (text[i + 1] === "#" || text[i + 1] === "@") && text[i + 2] === "{";
    if (!isQuotedVar) {
      result += text[i];
      i++;
      continue;
    }
    const openQuote = i;
    const varStart = i + 1;
    i += 3;
    let braceCount = 1;
    while (i < text.length && braceCount > 0) {
      if (text[i] === "{") braceCount++;
      else if (text[i] === "}") braceCount--;
      i++;
    }
    const varEnd = i - 1;
    if (braceCount === 0 && text[i] === '"') {
      result += text.slice(varStart, varEnd + 1);
      i++;
    } else {
      result += text.slice(openQuote, i);
    }
  }
  return result;
}

/** Normalize Unicode/smart quotes and braces to ASCII so variable syntax is always recognized. */
function normalizeToAscii(template: string): string {
  return template
    .replace(/\u201C|\u201D/g, '"') // " " (curly double quotes) -> "
    .replace(/\u2018|\u2019/g, "'") // ' ' (curly single quotes) -> '
    .replace(/\uFF5B/g, "{") // ｛ fullwidth left brace
    .replace(/\uFF5D/g, "}"); // ｝ fullwidth right brace
}

export function parseTemplatePrompt(template: string, mentionOpts?: ParseTemplatePromptMentionsOpts): PmDoc {
  const ascii = normalizeToAscii(template);
  const withoutQuotedVars = stripQuotesAroundTemplateVars(ascii);
  // Normalize line endings
  const normalized = withoutQuotedVars.replace(/\r\n/g, "\n");

  // Split by single newlines
  const lines = normalized.split("\n");

  const content: PmNode[] = [];
  let bulletListBuffer: string[] = [];
  let orderedListBuffer: string[] = [];

  const flushBulletList = () => {
    if (bulletListBuffer.length > 0) {
      content.push(createBulletList(bulletListBuffer, mentionOpts));
      bulletListBuffer = [];
    }
  };

  const flushOrderedList = () => {
    if (orderedListBuffer.length > 0) {
      content.push(createOrderedList(orderedListBuffer, mentionOpts));
      orderedListBuffer = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const trimmedLine = line.trim();

    // Between an opening fence and its closing fence the content is verbatim code, so
    // list/paragraph parsing is suspended. An unterminated fence falls through as an ordinary line.
    const fenceMatch = FENCE_OPEN_REGEX.exec(trimmedLine);
    if (fenceMatch) {
      let close = -1;
      for (let j = i + 1; j < lines.length; j++) {
        if ((lines[j] ?? "").trim() === "```") {
          close = j;
          break;
        }
      }
      if (close !== -1) {
        flushBulletList();
        flushOrderedList();
        content.push(createCodeBlock(lines.slice(i + 1, close), fenceMatch[1] ?? ""));
        i = close;
        continue;
      }
    }

    if (trimmedLine.length === 0) {
      // Empty line = empty paragraph (creates visual gap)
      flushBulletList();
      flushOrderedList();
      content.push(createParagraph([]));
      continue;
    }

    if (isBulletLine(line)) {
      // Accumulate bullet list items
      flushOrderedList();
      bulletListBuffer.push(line);
      continue;
    }

    if (isOrderedListLine(line)) {
      // Accumulate ordered list items
      flushBulletList();
      orderedListBuffer.push(line);
      continue;
    }

    // Regular paragraph
    flushBulletList();
    flushOrderedList();
    content.push(createParagraph(parseInlineContent(trimmedLine, mentionOpts)));
  }

  // Flush any remaining list items
  flushBulletList();
  flushOrderedList();

  // Ensure at least one empty paragraph if no content
  if (content.length === 0) {
    content.push(createParagraph([]));
  }

  return {
    type: PmNodeType.Doc,
    content,
  };
}
